import { useEffect, useMemo, useState } from 'react'
import { completeSet, completeWorkout, loadWorkout, type WorkoutSession, type WorkoutSet } from './api'
import { getPreviousValues } from './history'
import { parseDecimalInput, parsePositiveIntegerInput } from './input'
import { readRestTimer, remainingRestSeconds, startRestTimer } from './restTimer'

function displaySetType(type: string) {
  if (type === 'warmup') return 'Aufwärmen'
  if (type === 'failure') return 'Muskelversagen'
  if (type === 'working' || type === 'work') return 'Arbeitssatz'
  return type
}

export function WorkoutScreen(props: {
  athleteId: string
  sessionId: string
  onFinished: () => void
}) {
  const [session, setSession] = useState<WorkoutSession | null>(null)
  const [sets, setSets] = useState<WorkoutSet[]>([])
  const [drafts, setDrafts] = useState<Record<string, { weight: string; reps: string }>>({})
  const [previous, setPrevious] = useState<Record<string, string>>({})
  const [remaining, setRemaining] = useState(0)
  const [saving, setSaving] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function refresh() {
    const loaded = await loadWorkout(props.sessionId)
    setSession(loaded.session)
    setSets(loaded.sets)
    setDrafts(Object.fromEntries(loaded.sets.map((set) => [set.id, {
      weight: set.weight_kg == null ? '' : String(set.weight_kg),
      reps: set.reps == null ? '' : String(set.reps),
    }])))
  }

  useEffect(() => { void refresh().catch((e) => setError(e instanceof Error ? e.message : 'Training konnte nicht geladen werden.')) }, [props.sessionId])

  useEffect(() => {
    const update = () => {
      const timer = readRestTimer()
      setRemaining(timer ? remainingRestSeconds(timer) : 0)
    }
    update()
    const id = window.setInterval(update, 1000)
    document.addEventListener('visibilitychange', update)
    return () => { window.clearInterval(id); document.removeEventListener('visibilitychange', update) }
  }, [])

  useEffect(() => {
    if (!session) return
    const unique = new Map<string, WorkoutSet>()
    for (const set of sets) {
      const key = `${set.exercise_id ?? set.exercise_name_snapshot}|${set.set_type}`
      if (!unique.has(key)) unique.set(key, set)
    }
    void Promise.all([...unique.entries()].map(async ([key, set]) => {
      const value = await getPreviousValues({
        athleteId: props.athleteId,
        context: {
          exerciseId: set.exercise_id,
          exerciseName: set.exercise_name_snapshot ?? '',
          setType: set.set_type,
          sourceDayId: session.source_day_id,
          sourceDayIndex: session.source_day_index,
        },
      })
      if (!value) return
      const parts = [value.weight != null ? `${value.weight} kg` : '', value.reps != null ? `${value.reps} Wdh.` : ''].filter(Boolean)
      setPrevious((current) => ({ ...current, [key]: parts.join(' × ') }))
    })).catch(() => undefined)
  }, [session?.id, sets.length])

  const groups = useMemo(() => {
    const result = new Map<number, WorkoutSet[]>()
    for (const set of sets) result.set(set.exercise_order, [...(result.get(set.exercise_order) ?? []), set])
    return [...result.entries()].sort(([a], [b]) => a - b)
  }, [sets])

  async function confirm(set: WorkoutSet) {
    const draft = drafts[set.id] ?? { weight: '', reps: '' }
    const weight = parseDecimalInput(draft.weight)
    const reps = parsePositiveIntegerInput(draft.reps)
    if ((set.tracking_type === 'weight_reps' || set.tracking_type === 'reps') && reps == null) {
      setError('Für einen abgeschlossenen Wiederholungssatz braucht es mindestens 1 Wiederholung.')
      return
    }
    setSaving(set.id)
    setError(null)
    try {
      const saved = await completeSet(set.id, { weightKg: weight, reps })
      setSets((current) => current.map((item) => item.id === saved.id ? saved : item))
      if (saved.rest_seconds > 0) startRestTimer(saved.rest_seconds)

      // First confirmed set of a new exercise seeds only following sets of the same type.
      setDrafts((current) => {
        const hasOlderComparable = Object.keys(previous).some((key) => key === `${saved.exercise_id ?? saved.exercise_name_snapshot}|${saved.set_type}`)
        if (hasOlderComparable) return current
        const next = { ...current }
        for (const candidate of sets) {
          if (candidate.exercise_order !== saved.exercise_order || candidate.set_index <= saved.set_index || candidate.set_type !== saved.set_type) continue
          const candidateDraft = next[candidate.id] ?? { weight: '', reps: '' }
          next[candidate.id] = {
            weight: candidateDraft.weight || draft.weight,
            reps: candidateDraft.reps || draft.reps,
          }
        }
        return next
      })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Satz konnte nicht gespeichert werden.')
    } finally {
      setSaving(null)
    }
  }

  async function finish() {
    if (!session) return
    setSaving('finish')
    try {
      await completeWorkout(session)
      props.onFinished()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Training konnte nicht abgeschlossen werden.')
    } finally { setSaving(null) }
  }

  if (!session) return <section className="card"><p>Training wird geladen…</p></section>

  return <>
    <section className="workout-header card">
      <p className="eyebrow">AKTIVES TRAINING</p>
      <h1>{session.name}</h1>
      {remaining > 0 && <div className="timer">Pause {Math.floor(remaining / 60)}:{String(remaining % 60).padStart(2, '0')}</div>}
    </section>
    {error && <p className="error card">{error}</p>}
    {groups.map(([order, exerciseSets], groupIndex) => {
      const first = exerciseSets[0]
      const nextName = groups[groupIndex + 1]?.[1]?.[0]?.exercise_name_snapshot
      return <section className="card exercise-card" key={order}>
        <div className="exercise-heading"><span className="exercise-number">{order}</span><h2>{first.exercise_name_snapshot}</h2></div>
        {exerciseSets.map((set) => {
          const draft = drafts[set.id] ?? { weight: '', reps: '' }
          const previousKey = `${set.exercise_id ?? set.exercise_name_snapshot}|${set.set_type}`
          return <div className={`set-row ${set.status === 'completed' ? 'completed' : ''}`} key={set.id}>
            <div className="set-meta"><strong>Satz {set.set_index}</strong><small>{displaySetType(set.set_type)}</small>{previous[previousKey] && <small>Zuletzt: {previous[previousKey]}</small>}</div>
            {set.tracking_type !== 'reps' && <input inputMode="decimal" aria-label="Gewicht" placeholder="kg" value={draft.weight} disabled={set.status === 'completed'} onChange={(e) => setDrafts((c) => ({ ...c, [set.id]: { ...draft, weight: e.target.value } }))} />}
            {(set.tracking_type === 'weight_reps' || set.tracking_type === 'reps') && <input inputMode="numeric" aria-label="Wiederholungen" placeholder="Wdh." value={draft.reps} disabled={set.status === 'completed'} onChange={(e) => setDrafts((c) => ({ ...c, [set.id]: { ...draft, reps: e.target.value } }))} onKeyDown={(e) => { if (e.key === 'Enter' && set.status !== 'completed') void confirm(set) }} />}
            <button className="confirm" disabled={set.status === 'completed' || saving === set.id} onClick={() => void confirm(set)}>{set.status === 'completed' ? '✓' : 'Bestätigen'}</button>
          </div>
        })}
        {nextName && <p className="next-exercise">Nächste Übung: <strong>{nextName}</strong></p>}
      </section>
    })}
    <button className="finish-button" disabled={saving === 'finish'} onClick={() => void finish()}>Training abschliessen</button>
  </>
}
